import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { captureException } from "@sentry/react";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import AxisPicker from "./AxisPicker";
import { classifySeverity } from "./AxisPropertiesFields";
import ColorScalePicker from "./ColorScalePicker";
import CopyLabelsDialog from "./CopyLabelsDialog";
import FillCombinationsDialog from "./FillCombinationsDialog";
import LabelsDialog from "./LabelsDialog";
import { regionKey, useLidarClearance } from "./LidarClearanceContext";
import {
  createSystemAxisLink,
  deleteSystemAxisLink,
  updateSystemAxisLink,
  updateSystem as updateSystemRequest,
} from "./api";
import { colorForValue, colorScaleGapWarning } from "./colorScaleDisplay";
import {
  fetchLabels,
  fillLabels,
  updateLabel,
  type LidarClearanceLabel,
} from "./labelsApi";
import type { LidarClearanceSystem } from "./schemas";
import t from "./translations";

type Props = {
  system: LidarClearanceSystem | null;
  onClose: () => void;
};

export default function SystemDetailsDrawer({ system, onClose }: Props) {
  const { axes, systemAxisLinksBySystem, colorScaleByKey, refetch } =
    useLidarClearance();

  const [name, setName] = useState("");
  const [savingProperties, setSavingProperties] = useState(false);
  const [newAxisId, setNewAxisId] = useState<number | null>(null);
  const [addingAxis, setAddingAxis] = useState(false);
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [fillOpen, setFillOpen] = useState(false);
  const [copyAxisId, setCopyAxisId] = useState<number | null>(null);
  const [editingLabelIds, setEditingLabelIds] = useState<Set<number>>(
    new Set()
  );
  const [focusLabelId, setFocusLabelId] = useState<number | null>(null);
  const labelInputRefs = useRef(new Map<number, HTMLInputElement>());
  const originalLabelNames = useRef(new Map<number, string | null>());
  const [labels, setLabels] = useState<LidarClearanceLabel[]>([]);
  const [loadingLabels, setLoadingLabels] = useState(true);
  const [fillingAxisIds, setFillingAxisIds] = useState<Set<number>>(new Set());

  // autoFocus on every field entering edit mode at once would steal focus
  // from field to field as each mounts - focus only the specific field that
  // was double-clicked, once, after the whole group has actually rendered.
  useEffect(() => {
    if (focusLabelId === null) return;
    labelInputRefs.current.get(focusLabelId)?.focus();
    setFocusLabelId(null);
  }, [focusLabelId]);

  useEffect(() => {
    setName(system?.name ?? "");
    setNewAxisId(null);
  }, [system]);

  const loadLabels = useCallback(async () => {
    if (!system) return;
    setLoadingLabels(true);
    try {
      setLabels(await fetchLabels(system.region, system.id));
    } catch (error) {
      captureException(error, { event_id: "lidarClearance.fetchLabels" });
      toast.error(t.FailedToLoadLabels);
    } finally {
      setLoadingLabels(false);
    }
  }, [system]);

  useEffect(() => {
    loadLabels();
  }, [loadLabels]);

  const links = system
    ? systemAxisLinksBySystem.get(regionKey(system.region, system.id)) ?? []
    : [];
  const linkedAxisIds = useMemo(
    () => new Set(links.map((entry) => entry.axis.id)),
    [links]
  );
  const linkedAxes = useMemo(() => links.map((entry) => entry.axis), [links]);

  // Severity only has a well-defined meaning for a label curating a single
  // axis value - a combined label spans several axes and stays out of this
  // grouped view entirely (still visible, without severity, via Edit labels).
  const individualLabelsByAxisId = useMemo(() => {
    const map = new Map<number, LidarClearanceLabel[]>();
    for (const label of labels) {
      if (label.axis_values.length !== 1) continue;
      const axisId = label.axis_values[0].axis_id;
      const existing = map.get(axisId);
      if (existing) existing.push(label);
      else map.set(axisId, [label]);
    }
    // Max value (most severe) on top, matching the Axes tab's own
    // top-to-bottom Critical -> No issue threshold order.
    for (const axisLabels of map.values()) {
      axisLabels.sort(
        (a, b) => b.axis_values[0].axis_value - a.axis_values[0].axis_value
      );
    }
    return map;
  }, [labels]);

  if (!system) return null;

  const propertiesDirty = name.trim() !== system.name;
  const canSaveProperties =
    propertiesDirty && !savingProperties && name.trim().length > 0;

  const saveProperties = async () => {
    setSavingProperties(true);
    try {
      await updateSystemRequest(system.region, system.id, {
        name: name.trim(),
      });
      await refetch();
    } catch (error) {
      captureException(error, { event_id: "lidarClearance.updateSystem" });
      toast.error(t.FailedToUpdateSystem);
    } finally {
      setSavingProperties(false);
    }
  };

  const addAxis = async () => {
    if (newAxisId === null) return;
    const axis = axes.find(
      (candidate) =>
        candidate.id === newAxisId && candidate.region === system.region
    );
    if (!axis) return;
    setAddingAxis(true);
    try {
      // apps/api never falls back to the axis's own color_scale, so the link
      // must always carry a concrete value rather than null.
      await createSystemAxisLink(
        system.region,
        system.id,
        newAxisId,
        axis.color_scale
      );
      await refetch();
      setNewAxisId(null);
    } catch (error) {
      captureException(error, { event_id: "lidarClearance.linkAxis" });
      toast.error(t.FailedToLinkAxis);
    } finally {
      setAddingAxis(false);
    }
  };

  const changeLinkColorScale = async (linkId: number, value: number) => {
    try {
      await updateSystemAxisLink(system.region, system.id, linkId, value);
      await refetch();
    } catch (error) {
      captureException(error, { event_id: "lidarClearance.updateAxisLink" });
      toast.error(t.FailedToUpdateAxisLink);
    }
  };

  const removeLink = async (linkId: number) => {
    try {
      await deleteSystemAxisLink(system.region, system.id, linkId);
      await refetch();
    } catch (error) {
      captureException(error, { event_id: "lidarClearance.unlinkAxis" });
      toast.error(t.FailedToUnlinkAxis);
    }
  };

  const editLabelName = (labelId: number, name: string) => {
    setLabels((prev) =>
      prev.map((label) =>
        label.id === labelId ? { ...label, label_name: name } : label
      )
    );
  };

  const stopEditingLabels = (labelIds: number[]) => {
    labelIds.forEach((id) => originalLabelNames.current.delete(id));
    setEditingLabelIds((prev) => {
      const next = new Set(prev);
      labelIds.forEach((id) => next.delete(id));
      return next;
    });
  };

  const startEditingLabels = (labelIds: number[]) => {
    for (const id of labelIds) {
      if (!originalLabelNames.current.has(id)) {
        const label = labels.find((entry) => entry.id === id);
        originalLabelNames.current.set(id, label?.label_name ?? null);
      }
    }
    setEditingLabelIds((prev) => new Set([...prev, ...labelIds]));
  };

  // A field losing focus only locks its whole axis group back to plain text
  // when focus isn't moving to another field in that same group - clicking
  // from one label to the next within an axis should keep editing all of them.
  const handleLabelFieldBlur = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    axisId: number,
    axisLabelIds: number[]
  ) => {
    const staysInGroup =
      event.relatedTarget?.getAttribute("data-axis-group") ===
      String(axisId);
    if (!staysInGroup) {
      stopEditingLabels(axisLabelIds);
    }
  };

  const saveLabelName = async (label: LidarClearanceLabel) => {
    const normalized = label.label_name?.trim() || null;
    if (normalized === (originalLabelNames.current.get(label.id) ?? null)) {
      return;
    }
    try {
      const updated = await updateLabel(system.region, system.id, label.id, {
        label_name: normalized,
      });
      setLabels((prev) =>
        prev.map((entry) => (entry.id === updated.id ? updated : entry))
      );
    } catch (error) {
      captureException(error, { event_id: "lidarClearance.updateLabelName" });
      toast.error(t.FailedToSaveLabel);
      await loadLabels();
    }
  };

  // Fills just this one axis - the drawer's own "Fill Combinations" button is
  // reserved for multi-axis combinations, since every linked axis already
  // gets this quick single-axis action in its own section.
  const fillAxis = async (axisId: number) => {
    setFillingAxisIds((prev) => new Set(prev).add(axisId));
    try {
      const created = await fillLabels(system.region, system.id, [axisId]);
      setLabels((prev) => [...prev, ...created].sort((a, b) => a.id - b.id));
    } catch (error) {
      captureException(error, { event_id: "lidarClearance.fillAxisLabels" });
      toast.error(t.FailedToFillLabels);
    } finally {
      setFillingAxisIds((prev) => {
        const next = new Set(prev);
        next.delete(axisId);
        return next;
      });
    }
  };

  return (
    <Drawer anchor="right" open={system !== null} onClose={onClose}>
      <Box sx={{ width: 460, p: 3 }} data-testid="system-details-drawer">
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <TextField
            label={t.NameLabel}
            value={name}
            onChange={(event) => setName(event.target.value)}
            sx={{ flexGrow: 1 }}
            slotProps={{ htmlInput: { "data-testid": "system-drawer.name" } }}
          />
          <Tooltip title={t.SaveButton}>
            <span>
              <IconButton
                color="primary"
                disabled={!canSaveProperties}
                onClick={saveProperties}
                data-testid="system-drawer.save"
              >
                <SaveIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" gutterBottom>
          {t.LinkedAxesTitle}
        </Typography>
        {links.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t.NoAxesLinked}
          </Typography>
        ) : (
          <TableContainer component={Paper} data-testid="linked-axes-table">
            <Table size="small" sx={{ "& .MuiTableCell-root": { px: 1 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t.LinkedAxesNameColumn}</TableCell>
                  <TableCell>{t.LinkedAxesColorScaleColumn}</TableCell>
                  <TableCell sx={{ width: 40 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {links.map((entry) => {
                  const effectiveScaleId =
                    entry.link.color_scale ?? entry.axis.color_scale;
                  const scale = colorScaleByKey.get(
                    regionKey(system.region, effectiveScaleId)
                  );
                  const gapWarning = colorScaleGapWarning(
                    entry.axis,
                    scale,
                    t.ColorScaleGapWarning
                  );

                  return (
                    <TableRow
                      key={entry.link.id}
                      data-testid={`system-drawer.linked-axis-row-${entry.axis.id}`}
                    >
                      <TableCell sx={{ wordBreak: "break-word" }}>
                        {entry.axis.name_en}
                      </TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: "center" }}
                        >
                          <ColorScalePicker
                            region={system.region}
                            min={entry.axis.min_value}
                            max={entry.axis.max_value}
                            value={entry.link.color_scale}
                            onChange={(value) =>
                              changeLinkColorScale(
                                entry.link.id,
                                value ?? entry.axis.color_scale
                              )
                            }
                            defaultOption={{
                              label: t.ColorScaleDefaultOption,
                              scaleId: entry.axis.color_scale,
                            }}
                          />
                          {gapWarning && (
                            <Tooltip title={gapWarning}>
                              <CancelIcon color="error" fontSize="small" />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          aria-label={t.RemoveAxisLink}
                          onClick={() => removeLink(entry.link.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 2, alignItems: "center" }}>
          <Box sx={{ flexGrow: 1 }}>
            <AxisPicker
              region={system.region}
              excludeAxisIds={linkedAxisIds}
              value={newAxisId}
              onChange={setNewAxisId}
            />
          </Box>
          <Button
            onClick={addAxis}
            disabled={newAxisId === null || addingAxis}
            variant="outlined"
          >
            {t.AddAxisButton}
          </Button>
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", justifyContent: "space-between" }}
        >
          <Typography variant="subtitle1">{t.LabelsTitle}</Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={() => setFillOpen(true)}
              disabled={linkedAxes.length < 2}
              data-testid="system-drawer.fill-combinations"
            >
              {t.FillCombinationsButton}
            </Button>
            <Tooltip title={t.OpenLabelsButton}>
              <IconButton
                onClick={() => setLabelsOpen(true)}
                aria-label={t.OpenLabelsButton}
                data-testid="system-drawer.open-labels"
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
        {loadingLabels ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : linkedAxes.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t.NoLabelsYet}
          </Typography>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ mt: 1 }}
            data-testid="system-drawer.labels-table"
          >
            <Table size="small" sx={{ "& .MuiTableCell-root": { px: 1 } }}>
              <TableBody>
                {links.map((entry) => {
                  const axis = entry.axis;
                  const axisLabels = individualLabelsByAxisId.get(axis.id) ?? [];
                  const axisLabelIds = axisLabels.map((label) => label.id);
                  const effectiveScaleId =
                    entry.link.color_scale ?? entry.axis.color_scale;
                  const scale = colorScaleByKey.get(
                    regionKey(system.region, effectiveScaleId)
                  );
                  const isFillingAxis = fillingAxisIds.has(axis.id);

                  return (
                    <Fragment key={axis.id}>
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={0.5}
                              sx={{ alignItems: "center" }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700 }}
                              >
                                {axis.name_en}
                              </Typography>
                              <IconButton
                                size="small"
                                aria-label={t.EditLabelName}
                                disabled={axisLabelIds.length === 0}
                                onClick={() =>
                                  startEditingLabels(axisLabelIds)
                                }
                              >
                                <EditIcon fontSize="inherit" />
                              </IconButton>
                            </Stack>
                            <Stack direction="row" spacing={1}>
                              <Button
                                size="small"
                                onClick={() => fillAxis(axis.id)}
                                disabled={isFillingAxis}
                                startIcon={
                                  isFillingAxis ? (
                                    <CircularProgress size={14} />
                                  ) : undefined
                                }
                                data-testid={`system-drawer.fill-axis-${axis.id}`}
                              >
                                {t.FillAxisButton}
                              </Button>
                              <Button
                                size="small"
                                onClick={() => setCopyAxisId(axis.id)}
                              >
                                {t.CopyLabelsButton}
                              </Button>
                            </Stack>
                          </Stack>
                        </TableCell>
                      </TableRow>
                      {axisLabels.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                            >
                              {t.NoIndividualLabelsForAxis}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        axisLabels.map((label) => {
                          const value = label.axis_values[0].axis_value;
                          const color = colorForValue(scale, value);
                          const severity =
                            classifySeverity(axis, value)?.label ??
                            t.SeverityNoIssueLabel;
                          const isEditing = editingLabelIds.has(label.id);
                          return (
                            <TableRow key={label.id}>
                              <TableCell>
                                {isEditing ? (
                                  <TextField
                                    variant="standard"
                                    size="small"
                                    fullWidth
                                    value={label.label_name ?? ""}
                                    placeholder={String(value)}
                                    inputRef={(el: HTMLInputElement | null) => {
                                      if (el) {
                                        labelInputRefs.current.set(label.id, el);
                                      } else {
                                        labelInputRefs.current.delete(label.id);
                                      }
                                    }}
                                    slotProps={{
                                      htmlInput: {
                                        "data-axis-group": axis.id,
                                      },
                                    }}
                                    onChange={(event) =>
                                      editLabelName(label.id, event.target.value)
                                    }
                                    onBlur={(event) => {
                                      saveLabelName(label);
                                      handleLabelFieldBlur(
                                        event,
                                        axis.id,
                                        axisLabelIds
                                      );
                                    }}
                                    onKeyDown={(event) => {
                                      if (event.key === "Enter") {
                                        event.currentTarget.blur();
                                      }
                                    }}
                                  />
                                ) : (
                                  <Typography
                                    variant="body2"
                                    onDoubleClick={() => {
                                      startEditingLabels(axisLabelIds);
                                      setFocusLabelId(label.id);
                                    }}
                                    sx={{ cursor: "text" }}
                                  >
                                    {label.label_name ?? value}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  sx={{ alignItems: "center" }}
                                >
                                  <Typography variant="body2">
                                    {value}
                                  </Typography>
                                  <Box
                                    sx={{
                                      width: 14,
                                      height: 14,
                                      borderRadius: "50%",
                                      bgcolor: color ?? "grey.300",
                                      border: "1px solid",
                                      borderColor: "divider",
                                      flexShrink: 0,
                                    }}
                                  />
                                </Stack>
                              </TableCell>
                              <TableCell align="right">{severity}</TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <LabelsDialog
          open={labelsOpen}
          system={system}
          axes={linkedAxes}
          labels={labels}
          setLabels={setLabels}
          loading={loadingLabels}
          onClose={() => setLabelsOpen(false)}
        />
        <FillCombinationsDialog
          open={fillOpen}
          axes={linkedAxes}
          labels={labels}
          region={system.region}
          systemId={system.id}
          onClose={() => setFillOpen(false)}
          onFilled={(created) =>
            setLabels((prev) => [...prev, ...created].sort((a, b) => a.id - b.id))
          }
        />
        <CopyLabelsDialog
          open={copyAxisId !== null}
          axes={linkedAxes}
          region={system.region}
          systemId={system.id}
          initialAxisId={copyAxisId ?? undefined}
          onClose={() => setCopyAxisId(null)}
          onCopied={(created) =>
            setLabels((prev) => [...prev, ...created].sort((a, b) => a.id - b.id))
          }
        />
      </Box>
    </Drawer>
  );
}
